import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { useEffect, useState } from 'react'
import ECharts from '~/components/echarts'
import {
  awsSummarySavingsOrganizationOptimizerAWSMasterOptimizationsPath,
  rdsCostAnalysisChartOrganizationOptimizerAWSMasterOptimizationsPath,
  rdsInstanceClassChartsOrganizationOptimizerAWSMasterOptimizationsPath,
  rdsLicensedRiChartOrganizationOptimizerAWSMasterOptimizationsPath,
  rdsOpenSourceRiChartOrganizationOptimizerAWSMasterOptimizationsPath,
  rdsOptimizedInstanceClassChartsOrganizationOptimizerAWSMasterOptimizationsPath,
  rdsSavingsPlansChartOrganizationOptimizerAWSMasterOptimizationsPath,
} from '~/routes'
import { csrfHeader } from '~/utils/csrf'

export default function RDSOptimization({ organizationId, masterOptimizationId, masterOptimization }) {
  const [activeTab, setActiveTab] = useState('daily-cost')
  const [savingsData, setSavingsData] = useState(null)

  // biome-ignore lint/correctness/useExhaustiveDependencies: todo: re-evaluate dependencies
  useEffect(() => {
    if (masterOptimization) {
      fetchSavingsData()
    }
  }, [masterOptimization])

  const fetchSavingsData = async () => {
    try {
      const savingsUrl = awsSummarySavingsOrganizationOptimizerAWSMasterOptimizationsPath(organizationId)
      const response = await fetch(savingsUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...csrfHeader(),
        },
        body: JSON.stringify({ master_optimization_id: masterOptimizationId }),
      })

      if (response.ok) {
        const data = await response.json()
        setSavingsData(data)
      } else {
        console.error('Failed to fetch RDS savings data:', response.status, response.statusText)
      }
    } catch (error) {
      console.error('Error fetching RDS savings data:', error)
    }
  }

  if (!masterOptimization) {
    return null
  }

  const handleTabChange = (tabId) => {
    setActiveTab(tabId)
  }

  const dailySavings = savingsData?.rds_savings?.daily_savings || 0
  const savingsPercentage = savingsData?.rds_savings?.savings_percentage || 0

  return (
    <div className='row mb-4'>
      <div className='col-12'>
        <div className='card'>
          <div className='card-header'>
            <h5 className='card-title mb-0'>
              <Trans>
                RDS Optimization - Daily Savings ${dailySavings.toFixed(2)} ({savingsPercentage.toFixed(1)}%)
              </Trans>
            </h5>
          </div>
          <div className='card-body'>
            <ul className='nav nav-tabs mb-3'>
              <li className='nav-item'>
                <button
                  className={`nav-link ${activeTab === 'daily-cost' ? 'active' : ''}`}
                  onClick={() => handleTabChange('daily-cost')}
                  type='button'
                >
                  <Trans>Daily Cost</Trans>
                </button>
              </li>
              <li className='nav-item'>
                <button
                  className={`nav-link ${activeTab === 'db-instance' ? 'active' : ''}`}
                  onClick={() => handleTabChange('db-instance')}
                  type='button'
                >
                  <Trans>DB Instance</Trans>
                </button>
              </li>
              <li className='nav-item'>
                <button
                  className={`nav-link ${activeTab === 'rds-ris' ? 'active' : ''}`}
                  onClick={() => handleTabChange('rds-ris')}
                  type='button'
                >
                  <Trans>RDS RIs - Licensed</Trans>
                </button>
              </li>
              <li className='nav-item'>
                <button
                  className={`nav-link ${activeTab === 'rds-open-source-ris' ? 'active' : ''}`}
                  onClick={() => handleTabChange('rds-open-source-ris')}
                  type='button'
                >
                  <Trans>RDS RIs - Open Source</Trans>
                </button>
              </li>
              <li className='nav-item'>
                <button
                  className={`nav-link ${activeTab === 'saving-plans' ? 'active' : ''}`}
                  onClick={() => handleTabChange('saving-plans')}
                  type='button'
                >
                  <Trans>Saving Plans</Trans>
                </button>
              </li>
            </ul>

            <div className='mt-3'>
              {activeTab === 'daily-cost' && (
                <div className='wider-chart-container'>
                  <ECharts
                    title={t`Daily Cost Comparison`}
                    chartInfoTitle='aws_rds_optimization_daily_cost_comparison'
                    url={rdsCostAnalysisChartOrganizationOptimizerAWSMasterOptimizationsPath(organizationId)}
                    params={{ master_optimization_id: masterOptimizationId }}
                    hideLabels
                  />
                </div>
              )}

              {activeTab === 'db-instance' && (
                <div className='row'>
                  <div className='col-md-6'>
                    <ECharts
                      title={t`Current DB Instance Classes`}
                      chartInfoTitle='aws_rds_current_db_instance_classes'
                      url={rdsInstanceClassChartsOrganizationOptimizerAWSMasterOptimizationsPath(organizationId)}
                      params={{ master_optimization_id: masterOptimizationId }}
                    />
                  </div>
                  <div className='col-md-6'>
                    <ECharts
                      title={t`Optimized DB Instance Classes`}
                      chartInfoTitle='aws_rds_optimized_db_instance_classes'
                      url={rdsOptimizedInstanceClassChartsOrganizationOptimizerAWSMasterOptimizationsPath(
                        organizationId,
                      )}
                      params={{ master_optimization_id: masterOptimizationId }}
                    />
                  </div>
                </div>
              )}

              {activeTab === 'rds-ris' && (
                <ECharts
                  title={t`RDS Reserved Instances - Licensed`}
                  chartInfoTitle='aws_rds_reserved_instances_licensed'
                  url={rdsLicensedRiChartOrganizationOptimizerAWSMasterOptimizationsPath(organizationId)}
                  params={{ master_optimization_id: masterOptimizationId }}
                />
              )}

              {activeTab === 'rds-open-source-ris' && (
                <ECharts
                  title={t`RDS Reserved Instances - Open Source`}
                  chartInfoTitle='aws_rds_reserved_instances_open_source'
                  url={rdsOpenSourceRiChartOrganizationOptimizerAWSMasterOptimizationsPath(organizationId)}
                  params={{ master_optimization_id: masterOptimizationId }}
                />
              )}

              {activeTab === 'saving-plans' && (
                <ECharts
                  title={t`Database Saving Plans`}
                  chartInfoTitle='aws_rds_database_saving_plans'
                  url={rdsSavingsPlansChartOrganizationOptimizerAWSMasterOptimizationsPath(organizationId)}
                  params={{ master_optimization_id: masterOptimizationId }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
